import React from "react";
import { Trans } from "@lingui/macro";
import { graphql, PageProps } from "gatsby";
import { DestinationPageQuery } from "types/graphql-types";
import Herospace from "src/components/destinations/Herospace";
import AllInclusiveBox from "components/destinations/AllInclusiveBox/AllInclusiveBox";
import FindYourCruise from "components/FindYourCruise";

import TravelGuide from "src/components/destinations/TravelGuide";
import {
  DescriptionTextColumnContent,
  DescriptionTextColumnContentLeft,
  DescriptionTextColumnContentRight,
  DescriptionTextColumnLeadIn,
  DestinationColor,
  SectionTitleHeading
} from "@silversea-ssc/ui-components-react";
import OpenGraph from "components/SEO/OpenGraph";
import { DescriptionTextColumn } from "@silversea-ssc/ui-components-react";
import { SectionContainer } from "@silversea-ssc/ui-components-react";
import { Button } from "@silversea-ssc/ui-components-react";
import { ResponsiveSlideshow } from "components/Slideshow/Slideshow";
import InlineCTABox, {
  InlineCTABoxButton,
  InlineCTABoxContent
} from "components/InlineCTABox/InlineCTABox";
import ReadMore from "components/ReadMore/ReadMore";
import Slider, { SliderSlide } from "components/Slider";
import AsCardSlide from "components/Card/AsCardSlide";
import { SectionTitle } from "@silversea-ssc/ui-components-react";
import ShipCard from "components/ShipCard/ShipCard";
import { useFYC } from "components/FindYourCruise/useFYC";
import FYCQueryBuilder from "components/FindYourCruise/FYCQueryBuilder";
import TextBox from "components/TextBox/TextBox";
import { isPresent } from "src/utils/checks";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";

const DestinationPageTemplate: React.FC<PageProps<DestinationPageQuery>> = (
  props
) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const destinationData = props.data.destination!;
  const subDestinations = props.data.subDestinations?.nodes;
  const hasSubDestinations =
    subDestinations &&
    subDestinations.length > 0 &&
    subDestinations.some((d) => d.fullPath);
  const { destinationId } = destinationData;
  const ships = destinationData.visitingShips?.filter(isPresent);

  const geolocation = useGeolocation();
  const fycQuery = new FYCQueryBuilder()
    .filterByDestinationId(destinationId)
    .generate();

  const {
    searchClient,
    filterDefaultSearch,
    searchState,
    createURL,
    onSearchStateChange,
    triggerSearch,
    localizedSearchIndexName
  } = useFYC(fycQuery, geolocation.defaultCurrency);

  const triggerSpecialTypeCruisesSearch = () => {
    const newSearch = {
      toggle: {
        "content.specialType": true
      }
    };
    triggerSearch(newSearch);
    window.location.hash = "#find-your-cruise";
  };

  const slideshow = destinationData?.slideshowImages
    ?.map((i) => i?.imageWithCaption)
    .filter(isPresent);

  return (
    <React.Fragment>
      <OpenGraph
        openGraph={destinationData.metadata?.openGraph}
        excludeFromSitemap={destinationData.metadata?.excludeFromSitemap}
      />

      <DestinationColor destinationId={destinationId} id="root">
        <Herospace
          {...destinationData}
          searchClient={searchClient}
          initialSearchQuery={filterDefaultSearch}
          defaultSearchIndexName={localizedSearchIndexName}
        />
        <SectionContainer
          backgroundColor="destinationExtralight"
          spacingTop="regular"
          spacingBottom="compact"
          dataContentName="destination_introduction"
        >
          <DescriptionTextColumn>
            <DescriptionTextColumnLeadIn>
              {destinationData.leadIn?.localized}
            </DescriptionTextColumnLeadIn>
            <DescriptionTextColumnContent>
              <DescriptionTextColumnContentLeft>
                <ReadMore
                  backgroundColor="neutral05"
                  openLabel={
                    <Trans id="Keep Reading (read more)">Keep Reading</Trans>
                  }
                  closeLabel={<Trans id="Close (read more)">Close</Trans>}
                >
                  <TextBox
                    fontSize="medium"
                    content={destinationData.body?.localized}
                  />

                  {hasSubDestinations && (
                    <Button
                      variant="link"
                      href="#relatedDestinations"
                      data-content-piece="ANCHOR-RELATED-DESTINATIONS"
                    >
                      <Trans>View related destinations</Trans>
                    </Button>
                  )}
                </ReadMore>
              </DescriptionTextColumnContentLeft>
              <DescriptionTextColumnContentRight>
                {slideshow && (
                  <ResponsiveSlideshow
                    items={slideshow}
                    variant="white"
                    hideCaption={false}
                  />
                )}
                <br />
                <InlineCTABox align="right">
                  <InlineCTABoxContent>
                    {destinationData.travelGuideQuestion?.localized}
                  </InlineCTABoxContent>
                  <InlineCTABoxButton>
                    <Button
                      centerText
                      variant="primary"
                      href={destinationData.travelGuideUrl?.localized}
                      data-content-piece="view_the_brochures"
                    >
                      <Trans>Request a Brochure</Trans>
                    </Button>
                  </InlineCTABoxButton>
                </InlineCTABox>
              </DescriptionTextColumnContentRight>
            </DescriptionTextColumnContent>
          </DescriptionTextColumn>
        </SectionContainer>

        <div id="allInclusive">
          <AllInclusiveBox
            {...destinationData}
            triggerSpecialTypeCruisesSearch={triggerSpecialTypeCruisesSearch}
          />
        </div>

        <div id="find-your-cruise">
          <FindYourCruise
            title={<Trans>Find your cruise</Trans>}
            showTitle={true}
            titleAlign="left"
            backgroundColor="destination"
            searchClient={searchClient}
            initialSearchQuery={filterDefaultSearch}
            resultsPageSize={8}
            defaultSearchIndexName={localizedSearchIndexName}
            searchState={searchState}
            onSearchStateChange={onSearchStateChange}
            createURL={createURL}
            showFreeTextSearch={true}
            showExpeditionFilter={
              destinationData.fycShowExpeditionFilter || false
            }
            specialTypeFilter={destinationData.fycSpecialType}
            showDestinationFilter={false}
            showShipFilter={true}
            showDurationFilter={true}
            showOthersFilter={true}
            showPromosFilter={true}
          />
        </div>

        <TravelGuide
          sectionTitle={destinationData?.travelGuideTitle?.localized}
          image={destinationData?.travelGuideImage}
          guideTitle={destinationData?.travelGuideHeader?.localized}
          content={destinationData?.travelGuideBody?.localized}
          url={destinationData?.travelGuideUrl?.localized}
          ctaText={destinationData?.travelGuideButtonText?.localized}
        />

        {ships && ships.length > 0 && (
          <div id="luxuryShips" data-content-name="ship_cards">
            <SectionContainer
              backgroundColor="destinationExtralight"
              spacingTop="regular"
              spacingBottom="regular"
            >
              <SectionTitle variant="column">
                <SectionTitleHeading>
                  {destinationData.shipsSectionTitle?.localized}
                </SectionTitleHeading>
              </SectionTitle>

              {ships.map((ship, index) => (
                <ShipCard
                  key={ship.id}
                  variant={index % 2 ? "left" : "right"}
                  ship={ship}
                />
              ))}
            </SectionContainer>
          </div>
        )}

        {hasSubDestinations && (
          <div
            id="relatedDestinations"
            data-content-name="sub_destinations_slider"
          >
            <SectionContainer
              backgroundColor="white"
              spacingTop="regular"
              spacingBottom="regular"
            >
              <SectionTitle variant="wide">
                <SectionTitleHeading>
                  <Trans>Related Destinations</Trans>
                </SectionTitleHeading>
              </SectionTitle>
              <Slider>
                {subDestinations.map((subDestination) => {
                  const card = subDestination.card;
                  if (!card) return null;
                  return (
                    <SliderSlide key={subDestination.id}>
                      <AsCardSlide
                        fullPath={subDestination.fullPath}
                        card={card}
                        cardBackgroundColor="neutral05"
                        trackingName={subDestination.metadata?.tracking?.name}
                      />
                    </SliderSlide>
                  );
                })}
              </Slider>
            </SectionContainer>
          </div>
        )}
      </DestinationColor>
    </React.Fragment>
  );
};

export const query = graphql`
  fragment VisitingShip on SanityShip {
    id
    crewCount
    _rawDescription
    _rawDescription
    card {
      ...AsCard
    }
    fullPath
    diningOptions
    image {
      ...ImageWithPreview
    }
    deckPlan {
      asset {
        url
      }
    }
    lastRefurbishmentYear
    name
    passengerCount
    shipType
    suiteCount
    facilities {
      _key
      name {
        localized
      }
    }
  }

  query DestinationPage($id: String) {
    destination: sanityDestination(id: { eq: $id }) {
      metadata {
        excludeFromSitemap
        openGraph {
          ...OpenGraphMetaTags
        }
      }
      destinationId
      name {
        localized
      }
      title {
        localized
      }
      featureText1 {
        localized
      }
      featureText2 {
        localized
      }
      featureText3 {
        localized
      }
      heroImage {
        ...HeroResponsiveImage
      }
      leadIn {
        localized
      }

      body {
        localized
      }
      _rawBody
      slideshowImages {
        imageWithCaption {
          ...ImageWithCaptionAndPreview
        }
      }
      travelGuideBody {
        localized
      }
      travelGuideButtonText {
        localized
      }
      travelGuideHeader {
        localized
      }
      travelGuideQuestion {
        localized
      }
      filterSpecialCruisesLinkText {
        localized
      }
      packagesTitle {
        localized
      }
      packagesHeaderContent {
        localized
      }
      allInclusiveDisclaimer {
        localized
      }
      packages {
        name {
          localized
        }

        items {
          icon
          isHighlighted
          name {
            localized
          }
          description {
            localized
          }
          filters {
            ...AirNonAirOrLocationFilter
          }
          filterExclusions {
            ...LocationFilter
          }
        }
        showSpecialCruisesFilterLink
      }
      travelGuideImage {
        ...Image
      }
      travelGuideTitle {
        localized
      }
      travelGuideUrl {
        localized
      }
      video {
        file {
          videoId
        }
        trackingTitle
      }
      shipsSectionTitle {
        localized
      }
      visitingShips {
        ...VisitingShip
      }

      fycShowExpeditionFilter
      fycSpecialType
    }
    subDestinations: allSanitySubDestination(
      sort: { fields: card___title___en }
      filter: { destination: { id: { eq: $id } } }
    ) {
      nodes {
        id
        fullPath
        card {
          ...AsCard
        }
        metadata {
          tracking {
            name
          }
        }
      }
    }
  }
`;

export default React.memo(DestinationPageTemplate);
